import mixpanel, { type Config, type Mixpanel } from 'mixpanel-browser';
import shortHash from 'shorthash2';

import { isStatisticsEnabled } from '../utils/cookiebot';
import { AnalyticsEvents } from '../utils/customevents';
import { subscribeToPageChange } from '../utils/subscribeToPageChange';
import type { AnalyticsInterface } from './AnalyticsInterface';

interface AnalyticsOptions {
  isProduction: boolean;
  shouldTrackPageView?: (url: string) => boolean;
}

interface TrackEventsOptions {
  flush?: boolean;
}

type InitializationState = 'idle' | 'ready' | 'failed';

export class MixpanelAnalytics<E extends string, T extends Record<string, unknown>> implements AnalyticsInterface<E, T> {
  private initializationState: InitializationState = 'idle';
  private isAutomaticPageViewEnabled = false;
  private client: Mixpanel | undefined;

  constructor(
    private readonly token: string,
    private readonly analyticsOptions: AnalyticsOptions,
    private readonly mixpanelConfig: Partial<Config> = {},
    private readonly instanceName?: string
  ) {}

  init() {
    if (this.initializationState !== 'idle') {
      return;
    }

    const config: Partial<Config> = {
      api_host: 'https://api-eu.mixpanel.com',
      ignore_dnt: true,
      debug: !this.analyticsOptions.isProduction,
      verbose: !this.analyticsOptions.isProduction,
      disable_persistence: true,
      track_pageview: false,
      record_mask_text_selector: '.sensitive-string',
      record_sessions_percent: this.analyticsOptions.isProduction ? 0 : 100,
      ...this.mixpanelConfig,
    };

    try {
      if (this.instanceName === undefined) {
        mixpanel.init(this.token, config);
        this.client = mixpanel;
      } else {
        this.client = mixpanel.init(this.token, config, this.instanceName);
      }

      this.initializationState = 'ready';
    } catch (error) {
      this.disable(error);
    }
  }

  trackEvent(name: E, properties?: T, { flush = false }: TrackEventsOptions = {}) {
    if (!isStatisticsEnabled(this.analyticsOptions.isProduction)) {
      return;
    }

    this.runWithClient((client) => client.track(name, properties, { send_immediately: flush }));
  }

  trackButtonClick(properties?: T) {
    this.trackEvent(AnalyticsEvents.CLICK_BUTTON as E, properties);
  }

  trackPageView(properties?: T) {
    if (!isStatisticsEnabled(this.analyticsOptions.isProduction)) {
      return;
    }

    this.runWithClient((client) => client.track_pageview(properties));
  }

  setPropertiesForAllEvents(properties: T) {
    this.runWithClient((client) => client.register(properties));
  }

  identify(email: string, isKeyUser = false) {
    const domain = email.split('@')[1];
    const userId = `${shortHash(email)} (*****@${domain})`;

    this.runWithClient((client) => {
      client.identify(userId);
      client.people.set({ isKeyUser });
    });
  }

  enableAutomaticPageView() {
    if (!this.isAutomaticPageViewEnabled) {
      subscribeToPageChange((url) => {
        if (this.analyticsOptions.shouldTrackPageView?.(url) ?? true) {
          this.trackPageView();
        }
      });
      this.isAutomaticPageViewEnabled = true;
    }
  }

  private runWithClient(action: (client: Mixpanel) => void) {
    if (this.initializationState === 'idle') {
      this.init();
    }

    if (this.initializationState !== 'ready' || this.client === undefined) {
      return;
    }

    try {
      action(this.client);
    } catch (error) {
      // A single failed operation must not disable an otherwise healthy SDK.
      // eslint-disable-next-line no-console
      console.warn('Mixpanel analytics operation failed', error);
    }
  }

  private disable(error: unknown) {
    this.initializationState = 'failed';
    this.client = undefined;
    // Analytics must never turn a tracking failure into an application failure.
    // eslint-disable-next-line no-console
    console.warn('Mixpanel analytics is unavailable', error);
  }
}
